import api from '@/api'
import { HTTP_STATUS } from '@/helpers/constants'
import { comparadorString } from '@/helpers/FunctionHelper'
import {
  defineSyncSchema,
  schemaKinds,
  createSyncState,
  createSyncRequest,
  recordWrite,
  isStaleResponse,
  collectProtected,
  mergeSyncResponse,
  syncMutations
} from '@/helpers/storeSync'

const DISPOSITIVO_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_GEO_DISPOSITIVO', kind: 'dispositivo', insertAt: 'end' })

const state = {
  dispositivos: [],
  estadoConexion: null,
  geoResponsables: [],
  sync: createSyncState(schemaKinds(DISPOSITIVO_SYNC_SCHEMA)),
  estadoConexionSync: createSyncState(['estadoConexion'])
}

const getters = {
  dispositivos: (state) => state.dispositivos,
  estadoConexion: (state) => state.estadoConexion,
  dispositivosAsignados: (state) => state.dispositivos.filter(x => x.GEO_RECURSO_DISPOSITIVO && !x.GEO_RECURSO_DISPOSITIVO.FECHA_BAJA),
  getDispositivoByID: (state) => (idDispositivo) => {
    return idDispositivo
      ? state.dispositivos.find(x => x.ID_GEO_DISPOSITIVO.equalsIgnoreCase(idDispositivo))
      : null
  },
  getDispositivoByISSI: (state) => (issi) => {
    return issi
      ? state.dispositivos.find(x => x.CODIGO_EXTERNO.equalsIgnoreCase(issi))
      : null
  },
  dispositivosConPosicion: (state) => {
    return state.dispositivos.filter(x => x.GEO_POS_ACTUAL)
  },
  dispositivoByIdRecurso: (state) => (idRecurso) => {
    return state.dispositivos.filter(x => x.GEO_RECURSO_DISPOSITIVO && x.GEO_RECURSO_DISPOSITIVO.ID_RECURSO.equalsIgnoreCase(idRecurso) && !x.GEO_RECURSO_DISPOSITIVO.FECHA_BAJA)
  },
  dispositivoActivoByIdRecurso: (state, getters) => (idRecurso) => {
    const filtrados = getters.dispositivoByIdRecurso(idRecurso)
    return filtrados?.reduce((ultimo, actual) => {
      const fechaUltimo = new Date(ultimo.GEO_POS_ACTUAL?.FECHA)
      const fechaActual = new Date(actual.GEO_POS_ACTUAL?.FECHA)
      return fechaActual > fechaUltimo ? actual : ultimo
    }, filtrados[0])
  },
  geoResponsables: (state) =>
    [...state.geoResponsables].sort((a, b) => comparadorString.compare(a.NOMBRE, b.NOMBRE))
}

const mutations = {
  ...syncMutations,

  BEGIN_ESTADO_CONEXION_REQUEST(state) {
    state.estadoConexionSync.seq += 1
  },

  MERGE_DISPOSITIVOS(state, { rows, request }) {
    state.dispositivos = mergeSyncResponse(state.sync, state.dispositivos, rows, DISPOSITIVO_SYNC_SCHEMA, request)
  },

  MERGE_ESTADO_CONEXION(state, { value, request }) {
    if (isStaleResponse(state.estadoConexionSync, request)) return

    const protectedByKind = collectProtected(state.estadoConexionSync.stamps, request)
    if (!protectedByKind.estadoConexion?.CURRENT) {
      state.estadoConexion = value
    }
    state.estadoConexionSync.appliedSeq = request.seq
  },

  SOCKET_addDispositivo(state, data) {
    state.dispositivos.push(data)
    recordWrite(state.sync, 'dispositivo', data.ID_GEO_DISPOSITIVO)
  },

  SOCKET_updateDispositivo(state, data) {
     if (!data.ID_RECURSO) {
      const index = state.dispositivos.findIndex(
        d => d.ID_GEO_DISPOSITIVO === data.ID_GEO_DISPOSITIVO
      )

      if (index !== -1) {
        state.dispositivos.splice(index, 1, {
          ...state.dispositivos[index],
          ...data
        })
        recordWrite(state.sync, 'dispositivo', data.ID_GEO_DISPOSITIVO)
      }

      return
    }

    const idRecurso = data.ID_RECURSO
    const idDispositivoNuevo = data.ID_GEO_DISPOSITIVO

    state.dispositivos.forEach(d => {
      if (d.GEO_RECURSO_DISPOSITIVO?.ID_RECURSO === idRecurso) {
        d.GEO_RECURSO_DISPOSITIVO = null
        recordWrite(state.sync, 'dispositivo', d.ID_GEO_DISPOSITIVO)
      }
    })

    if (idDispositivoNuevo) {

      const index = state.dispositivos.findIndex(
        d => d.ID_GEO_DISPOSITIVO === idDispositivoNuevo
      )

      if (index !== -1) {
        const dispositivo = state.dispositivos[index]

        state.dispositivos.splice(index, 1, {
          ...dispositivo,
          GEO_RECURSO_DISPOSITIVO: data
        })
        recordWrite(state.sync, 'dispositivo', idDispositivoNuevo)
      }
    }
  },

  SOCKET_updateEstadoConexion(state, data) {
    state.estadoConexion = data.estado
    recordWrite(state.estadoConexionSync, 'estadoConexion', 'CURRENT')
  },

  SET_GEO_RESPONSABLES(state, data) {
    state.geoResponsables = data
  }
}

const actions = {
  async fetchDispositivos({ commit, state, getters, dispatch }) {
    try {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)
      const response = await api.dispositivo.getDispositivos()
      commit('MERGE_DISPOSITIVOS', { rows: response.data, request })
      dispatch('recurso/setDispositivosRecursos', getters.dispositivosAsignados, { root: true })
    } catch (err) {
      console.error('[dispositivo] Error al obtener el listado de dispositivos: ', err)
      throw err
    }
  },

  async fetchStatusConnection({ commit, state }) {
    try {
      commit('BEGIN_ESTADO_CONEXION_REQUEST')
      const request = createSyncRequest(state.estadoConexionSync.seq)
      const response = await api.dispositivo.getConnectionStatus()
      commit('MERGE_ESTADO_CONEXION', { value: response.data, request })
    } catch (error) {
      console.error('[dispositivo] Error al obtener el estado de la conexion: ', error)
      throw error
    }
  },

  async addDispositivo({ commit }, data) {
    try {
      await api.dispositivo.addDispositivo(data)
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  async updateDispositivo({ commit }, data) {
    try {
      await api.dispositivo.updateDispositivo(data)
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  async getTrackGeoDispositivo ({ _commit }, data) {
    try {
      const response = await api.dispositivo.getTrackFechasDispositivo(data)

      if (response.status !== HTTP_STATUS.OK) {
        return null
      }

      return response.data
    } catch (error) {
      console.error('[dispositivo] Error al obtener el track del dispositivo:', error)
      throw error
    }
  },

  async fetchDispositivoById ({ _commit }, idGeoDispositivo) {
    try {
      const response = await api.dispositivo.getDispositivoById(idGeoDispositivo)

      return response.data
    } catch (error) {
      if (error.response?.status === HTTP_STATUS.NOT_FOUND) {
        console.warn('[dispositivo] Dispositivo no encontrado:', idGeoDispositivo)
        return null
      }
      console.error('[dispositivo] Error al obtener el dispositivo:', error)
      throw error
    }
  },

  async fetchGeoResponsables({ commit }) {
    try {
      const response = await api.dispositivo.getGeoResponsables()
      commit('SET_GEO_RESPONSABLES', response.data)
    } catch (err) {
      console.error('[dispositivo] Error al obtener el catálogo de responsables:', err)
      throw err
    }
  }
}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
